/**
 * Payment Status Badge Component
 * Displays payment status with color-coded visual indicators
 *
 * @author SAUBHAGYA Development Team
 * @since Story 4-5
 *
 * @description
 * Visual badge component for displaying payment statuses with:
 * - Color-coded statuses (Gray/Blue/Green/Red)
 * - Multiple size variants (sm/md/lg)
 * - Animated processing state
 * - Full accessibility support (ARIA, dark mode)
 *
 * @example
 * ```tsx
 * // Basic usage
 * <PaymentStatusBadge status="COMPLETED" />
 *
 * // Large size
 * <PaymentStatusBadge status="PROCESSING" size="lg" />
 *
 * // Custom styling
 * <PaymentStatusBadge status="PENDING" className="ml-2" />
 * ```
 */

import { cn } from '@/lib/utils';

export type PaymentStatus = 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
type Size = 'sm' | 'md' | 'lg';

export interface PaymentStatusBadgeProps {
    /**
     * Payment status to display
     */
    status: PaymentStatus;

    /**
     * Visual size of the badge
     * @default 'md'
     */
    size?: Size;

    /**
     * Additional CSS classes
     */
    className?: string;
}

/**
 * Status configuration with colors and labels
 */
const statusConfig: Record<PaymentStatus, {
    label: string;
    bgColor: string;
    textColor: string;
    darkBgColor: string;
    darkTextColor: string;
    animate?: boolean;
    strikethrough?: boolean;
}> = {
    PENDING: {
        label: 'Pending',
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-800',
        darkBgColor: 'dark:bg-gray-800',
        darkTextColor: 'dark:text-gray-200'
    },
    PROCESSING: {
        label: 'Processing',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-800',
        darkBgColor: 'dark:bg-blue-900',
        darkTextColor: 'dark:text-blue-200',
        animate: true
    },
    COMPLETED: {
        label: 'Completed',
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
        darkBgColor: 'dark:bg-green-900',
        darkTextColor: 'dark:text-green-200'
    },
    FAILED: {
        label: 'Failed',
        bgColor: 'bg-red-100',
        textColor: 'text-red-800',
        darkBgColor: 'dark:bg-red-900',
        darkTextColor: 'dark:text-red-200'
    },
    CANCELLED: {
        label: 'Cancelled',
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-600',
        darkBgColor: 'dark:bg-gray-800',
        darkTextColor: 'dark:text-gray-400',
        strikethrough: true
    }
};

/**
 * Size configuration for badge dimensions
 */
const sizeConfig: Record<Size, {
    height: string;
    padding: string;
    fontSize: string;
}> = {
    sm: {
        height: 'h-5',
        padding: 'px-2',
        fontSize: 'text-xs'
    },
    md: {
        height: 'h-6',
        padding: 'px-2.5',
        fontSize: 'text-sm'
    },
    lg: {
        height: 'h-8',
        padding: 'px-3',
        fontSize: 'text-base'
    }
};

/**
 * Payment Status Badge Component
 *
 * @implements Status Colors (Gray/Blue/Green/Red with dark mode)
 * @implements Size Variants (sm/md/lg)
 * @implements Processing Animation (pulse effect)
 * @implements Cancelled Styling (strikethrough)
 * @implements Accessibility (role, aria-label, dark mode)
 */
export function PaymentStatusBadge({
    status,
    size = 'md',
    className
}: PaymentStatusBadgeProps) {
    const statusInfo = statusConfig[status];
    const sizeInfo = sizeConfig[size];

    const ariaLabel = `Payment status: ${statusInfo.label}`;

    return (
        <span
            role="status"
            aria-label={ariaLabel}
            className={cn(
                // Base styles
                'inline-flex items-center justify-center rounded-full font-medium',

                // Size variant styles
                sizeInfo.height,
                sizeInfo.padding,
                sizeInfo.fontSize,

                // Status colors (light mode)
                statusInfo.bgColor,
                statusInfo.textColor,

                // Dark mode support
                statusInfo.darkBgColor,
                statusInfo.darkTextColor,

                // Processing animation
                statusInfo.animate && 'animate-pulse',

                // Custom classes
                className
            )}
        >
            <span className={cn(statusInfo.strikethrough && 'line-through')}>
                {statusInfo.label}
            </span>
        </span>
    );
}

export default PaymentStatusBadge;
